'use client'

import { Suspense, useEffect, useState } from "react"
import Image from "next/image"
import Link from "next/link"
import { Button } from "@/components/ui/button"
import { ArrowRight, Camera, MapPin, ShieldCheck, Trophy, UserRoundCheck } from "lucide-react"
import { GrassTuft } from "@/components/icons/grass-tuft"
import {
  PhotoGrid,
  type CommunityGalleryFilters,
  type LawnPhoto,
} from "@/components/community/photo-grid"
import { FilterBar } from "@/components/community/filter-bar"
import { type LeaderboardPhoto } from "@/components/community/leaderboard-podium"
import { SubmitCta } from "@/components/community/submit-cta"
import { communityAnalytics } from "@/lib/analytics"

interface CommunityPageContentProps {
  initialFilters: CommunityGalleryFilters
  initialPage: number
  initialPhotos: LawnPhoto[]
  initialHasMore: boolean
  initialGalleryError: string | null
  featuredPhoto: LeaderboardPhoto | null
  featuredUnavailable: boolean
}

export function CommunityPageContent({
  initialFilters,
  initialPage,
  initialPhotos,
  initialHasMore,
  initialGalleryError,
  featuredPhoto,
  featuredUnavailable,
}: CommunityPageContentProps) {
  const [filters, setFilters] = useState<CommunityGalleryFilters>(initialFilters)

  useEffect(() => {
    communityAnalytics.galleryViewed()
  }, [])

  useEffect(() => {
    setFilters(initialFilters)
  }, [initialFilters])

  return (
    <div className="min-h-screen bg-[#f5f8f2]">
      <section className="relative overflow-hidden bg-green-950 text-white">
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: 'repeating-linear-gradient(112deg, transparent 0, transparent 72px, rgba(255,255,255,0.12) 72px, rgba(255,255,255,0.12) 144px)',
          }}
        />
        <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-4 py-14 sm:px-6 md:py-20 lg:grid-cols-[0.9fr_1.1fr] lg:px-8 lg:py-24">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3.5 py-2 text-xs font-bold uppercase tracking-[0.12em] text-green-50">
              <GrassTuft className="h-4 w-4 text-green-300" />
              Community photo gallery
            </div>

            <h1 className="font-editorial mt-6 text-5xl font-semibold leading-[0.96] tracking-[-0.025em] text-balance sm:text-6xl lg:text-7xl">
              Real lawn photos from real homeowners.
            </h1>

            <p className="font-editorial mt-5 text-2xl font-semibold text-green-100 sm:text-3xl">
              A good lawn deserves a little bragging.
            </p>
            <p className="mt-4 max-w-xl text-base leading-7 text-green-50/85 md:text-lg">
              Browse homeowner lawn renovations and progress by grass type and region, see what worked, and vote for community favorites. No account required.
            </p>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Button asChild size="lg" className="min-h-12 rounded-full bg-white px-6 text-green-950 shadow-lg hover:bg-green-50">
                <Link href="/community/submit">
                  <Camera className="mr-2 h-4 w-4" aria-hidden="true" />
                  Share your lawn
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="min-h-12 rounded-full border-white/40 bg-transparent px-6 text-white hover:bg-white hover:text-green-950">
                <Link href="/community/leaderboard">
                  <Trophy className="mr-2 h-4 w-4" aria-hidden="true" />
                  See the rankings
                </Link>
              </Button>
            </div>

            <ul className="mt-8 grid gap-3 text-sm text-green-50/80 sm:grid-cols-3 lg:grid-cols-1 xl:grid-cols-3">
              <li className="flex items-center gap-2">
                <UserRoundCheck className="h-4 w-4 shrink-0 text-green-300" aria-hidden="true" />
                No account needed
              </li>
              <li className="flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 shrink-0 text-green-300" aria-hidden="true" />
                Reviewed before posting
              </li>
              <li className="flex items-center gap-2">
                <MapPin className="h-4 w-4 shrink-0 text-green-300" aria-hidden="true" />
                Exact ZIP stays private
              </li>
            </ul>
          </div>

          <div className="relative mx-auto w-full max-w-2xl lg:mx-0">
            <div className="absolute -inset-3 rotate-2 rounded-[2rem] border border-white/15 bg-white/10" aria-hidden="true" />
            <div className="relative overflow-hidden rounded-[1.75rem] border border-white/20 bg-green-900 shadow-2xl">
              {featuredPhoto ? (
                <Link href={`/community/${featuredPhoto.slug || featuredPhoto.id}`} className="group block focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-green-300 focus-visible:ring-inset">
                  <div className="relative aspect-4/3 overflow-hidden">
                    <Image
                      src={featuredPhoto.image_url}
                      alt={featuredPhoto.title}
                      fill
                      loading="eager"
                      className="object-cover transition-transform duration-500 motion-reduce:transition-none group-hover:scale-[1.02]"
                      sizes="(max-width: 1024px) 100vw, 55vw"
                    />
                    <div className="absolute inset-0 bg-linear-to-t from-green-950/90 via-transparent to-transparent" />
                    <div className="absolute inset-x-0 bottom-0 p-5 sm:p-7">
                      <div className="inline-flex rounded-full bg-amber-300 px-3 py-1 text-[11px] font-black uppercase tracking-[0.12em] text-amber-950 shadow-sm">
                        Featured lawn
                      </div>
                      <div className="mt-3 flex items-end justify-between gap-4">
                        <div>
                          <h2 className="font-editorial text-2xl font-semibold leading-tight text-white sm:text-3xl">{featuredPhoto.title}</h2>
                          {featuredPhoto.grass_type && <p className="mt-1 text-sm text-green-100">{featuredPhoto.grass_type}</p>}
                        </div>
                        <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-white px-3 py-1.5 text-sm font-bold text-green-950">
                          {featuredPhoto.score} score
                          <ArrowRight className="h-4 w-4" aria-hidden="true" />
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>
              ) : (
                <div className="flex aspect-4/3 flex-col items-center justify-center px-8 text-center">
                  <div className="flex h-14 w-14 items-center justify-center rounded-full bg-white text-green-800">
                    <Camera className="h-6 w-6" aria-hidden="true" />
                  </div>
                  <h2 className="font-editorial mt-5 text-3xl font-semibold">Claim the first feature</h2>
                  <p className="mt-2 max-w-sm text-sm leading-6 text-green-100">
                    {featuredUnavailable
                      ? 'The featured lawn could not load. The gallery below may still be available.'
                      : 'Share your lawn and help give the gallery its opening look.'}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 md:py-14 lg:px-8">
        <section aria-labelledby="community-gallery-heading">
          <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.12em] text-green-700">From the neighborhood</p>
              <h2 id="community-gallery-heading" className="font-editorial mt-2 text-4xl font-semibold leading-none text-green-950 md:text-5xl">
                Real lawns, one board
              </h2>
            </div>
            <Link href="/community/leaderboard" className="inline-flex min-h-11 items-center gap-2 self-start rounded-full px-1 text-sm font-semibold text-green-800 hover:text-green-600 sm:self-auto">
              View leaderboard
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </div>

          <div className="mb-5 md:mb-8">
            <Suspense fallback={<div className="h-16 ds-glass rounded-3xl animate-pulse" />}>
              <FilterBar initialFilters={initialFilters} onFiltersChange={setFilters} />
            </Suspense>
          </div>

          <Suspense fallback={
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="ds-glass animate-pulse overflow-hidden rounded-3xl">
                  <div className="aspect-4/3 bg-green-100/70" />
                  <div className="p-4">
                    <div className="h-4 bg-white/70 rounded-full mb-2" />
                    <div className="h-3 bg-white/60 rounded-full" />
                  </div>
                </div>
              ))}
            </div>
          }>
            <PhotoGrid
              sortBy={filters.sortBy}
              grassType={filters.grassType}
              region={filters.region}
              initialFilters={initialFilters}
              initialPhotos={initialPhotos}
              initialPage={initialPage}
              initialHasMore={initialHasMore}
              initialError={initialGalleryError}
            />
          </Suspense>
        </section>

        <SubmitCta />
      </div>
    </div>
  )
}
